import React, { useEffect, useState } from "react";
import { 
	message,
	List,
	Drawer,
	Space,
	Popconfirm,
	Descriptions,
	Input,
	Button,
	Badge,
	Row,
	Col,
	theme
} from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";
import { get_organizations, edit_organization } from "../services";
import { hover_item } from "../hover";
import PropTypes from "prop-types";
import { CircleAnimationCompact } from "../components/loading";
import { useCalculateListSizeForPaginationHook } from "../hooks";

const empty_organization = {
	name: "",
	org_id: "",
	active: true,
};

export const Organizations = ({ token }) => {

	const { token: antToken } = theme.useToken();

	const [organizations, setOrganizations] = useState([]);
	const [selected, setSelected] = useState(empty_organization);
	const [loading, setLoading] = useState(true);
	const [loadingSubmit, setLoadingSubmit] = useState(false);
	const [loadingCreate, setLoadingCreate] = useState(false);
	const { pageSize } = useCalculateListSizeForPaginationHook(70);
	const [openEdit, setOpenEdit] = useState(false);
	const [openCreate, setOpenCreate] = useState(false);
	const [hoveredItem, setHoveredItem] = useState(null);

	const [newOrgName, setNewOrgName] = useState("");
	const [newOrgID, setNewOrgID] = useState("");

	const showEdit = (item) => {
		setSelected(item);
		setOpenEdit(true);
	};
	const showCreate = () => {
		setOpenCreate(true);
	};

	const onClose = () => {
		setOpenEdit(false);
		setSelected(empty_organization);
	};

	const onCloseCreate = () => {
		setOpenCreate(false);
	};

	const handleChangeOrg = (value, attribute) => {
		setSelected((orgSelected) => ({
			...orgSelected, [attribute]: value
		}));
	};
    
	const handleChangeNewName = (value) => {
		setNewOrgName(value);
	};
	const handleChangeNewID = (value) => {
		setNewOrgID(value);
	};

	const submitChanges = async (edit_value) => {
		setLoadingSubmit(true);

		const data_content = (value) => {
			if(value === "edit"){
				return {
					id: selected.id,
					name: selected.name,
					org_id: selected.org_id
				};                    
			}
			if(value === "deactivate"){
				return { id: selected.id };
			}
			return null;
		};

		try {
			const res = await edit_organization(token, edit_value, data_content(edit_value));

			if(res){
				message.success(res);

				setOrganizations(organizations.map(org => {
					if(org.id === selected.id){
						if(edit_value === "deactivate"){
							return { ...selected, active: false };
						}
                        
						return selected;
					}
					return org;
				}));            

				onClose();
			}
		} catch(error){
			console.error(error);
			message.error(error.message);
			onClose();
		}

		setLoadingSubmit(false);
	};

	const createOrganization = async () => {
        
		if (!newOrgName || !newOrgID) {
			message.error("Por favor Ingresa toda la información");
			return;
		}
		setLoadingCreate(true);
        
		const data = {
			name: newOrgName,
			org_id: newOrgID
		};

		try {
			const res = await edit_organization(token, "create", data);

			if(res){
				message.success(res);
				const newOrgs = organizations;
				newOrgs.push({
					name: newOrgName,
					org_id: newOrgID,
					active: true
				});
				setOrganizations(newOrgs); 

				setNewOrgID("");
				setNewOrgName("");

				onCloseCreate();
				setLoadingCreate(false);
			}
		} catch(error){
			console.error(error);
			message.error(error.message);
			onCloseCreate();
			setLoadingCreate(false);
		}
	};

	const reduceText = (text) => {
		if (text === "") {
			return "---";
		}
		if (text.length > 40) {
			return text.substring(0, 40) + "...";
		}
		return text;
	};

	useEffect(() => {
		const fetchData = async () => {
			try {
				const orgs = await get_organizations(token);
				setOrganizations(orgs);
				setLoading(false);
			} catch (error) {
				console.error(error.response?.data);
				message.error(error.response?.data?.message || "Error al cargar datos");
				setLoading(false);
			}
		};

		fetchData();
	}, [token]);

	return (
		<>
			{
				loading &&
			<CircleAnimationCompact/>
			}
			{
				!loading &&
			<>
				<Button 
					type="primary"
					onClick={showCreate}
				>Crear Organización</Button>
				<List
					pagination={{
						position: "top",
						align: "center",
						pageSize: pageSize,
						showSizeChanger: false,
						disabled: false,
					}}
					header={
						<Row
							style={{
								background: antToken.colorBgLayout,
								borderRadius: "10px"
							}}
						>
							<Col flex="auto">
								<List.Item key={0} >
									<List.Item.Meta 
										title="Nombre"
									/>
									<List.Item.Meta  
										style={{
											textAlign:"center",
										}} 
										title="ID" 
									/>
									<List.Item.Meta 
										style={{
											textAlign:"center",
										}} 
										title="Estado" 
									/>
								</List.Item>
							</Col>
						</Row>
					}
					dataSource={organizations}
					renderItem={(item, index) => (
						<List.Item
							key={index}
							onMouseEnter={() => setHoveredItem(index)}
							onMouseLeave={() => setHoveredItem(null)}
							onClick={() => {
								setSelected(item);
								showEdit(item);
							}}
							style={hover_item(hoveredItem === index, antToken, "invoices", 45)}
						>
							<List.Item.Meta
								title={reduceText(item.name)}
							/>
							<List.Item.Meta
								description={reduceText(item.org_id)}
							/>
							<List.Item.Meta
								title={<Badge status={item?.active? "success" : "default"} text={item.active?  "Activo" : "Inactivo"} />}
							/>
						</List.Item>
					)}
				/>
			</>
			}

			<Drawer
				title="Editar Organización"
				placement="right"
				width={800}
				onClose={onClose}
				open={openEdit}
				extra={
					<Space>
						<Popconfirm
							title="Desactivar Organización"
							description="¿Estás seguro que quieres desactivar esta organización?"
							onConfirm={() => { submitChanges("deactivate"); }}
							icon={<QuestionCircleOutlined style={{ color: "red" }} />}
						>
							<Button type="primary" disabled={!selected.active} loading={loadingSubmit} danger>
                                Desactivar
							</Button>
						</Popconfirm>
						<Button type="primary" disabled={!selected.active} loading={loadingSubmit} onClick={() => { submitChanges("edit"); }}>
                            Editar
						</Button>
					</Space>
				}
			>
				<Descriptions
					layout="vertical"
					labelStyle={{
						//color: "black",
						fontWeight: "bold",
					}}
				>
					<Descriptions.Item label={"Nombre"}>
						<Input
							rows={1}
							showCount
							status={(selected.name.trim() === "") ? "error" : ""}
							value={selected.name}
							onChange={(event) => handleChangeOrg(event.target.value, "name")}
							maxLength={15}
						/>
					</Descriptions.Item>
					<Descriptions.Item label={"ID de la Organización"}>
						<Input
							rows={1}
							showCount
							status={(selected.org_id.trim() === "") ? "error" : ""}
							value={selected.org_id}
							onChange={(event) => handleChangeOrg(event.target.value, "org_id")}
							maxLength={30}
						/>
					</Descriptions.Item>
				</Descriptions>
			</Drawer>

			<Drawer
				title="Crear Organización"
				placement="right"
				width={800}
				onClose={onCloseCreate}
				open={openCreate}
				extra={
					<Space>
						<Popconfirm
							title="Crear Organización"
							description="¿Estás seguro que quieres crear esta organización?"
							onConfirm={() => { createOrganization(); }}
							icon={<QuestionCircleOutlined style={{ color: "red" }} />}
						>
							<Button type="primary" loading={loadingCreate}>
                                Crear
							</Button>
						</Popconfirm>
					</Space>
				}
			>
				<Descriptions
					layout="vertical"
					labelStyle={{
						//color: "black",
						fontWeight: "bold",
					}}
				>
					<Descriptions.Item label={"Nombre"}>
						<Input
							rows={1}
							showCount
							status={(newOrgName.trim() === "") ? "error" : ""}
							value={newOrgName}
							onChange={(event) => handleChangeNewName(event.target.value)}
							maxLength={15}
						/>
					</Descriptions.Item>
					<Descriptions.Item label={"ID de la Organización"}>
						<Input
							rows={1}
							showCount
							status={(newOrgName.trim() === "") ? "error" : ""}
							value={newOrgID}
							onChange={(event) => handleChangeNewID(event.target.value)}
							maxLength={30}
						/>
					</Descriptions.Item>
				</Descriptions>

			</Drawer>
		</>
	);
};

Organizations.propTypes = {
	token: PropTypes.string.isRequired,
};
